/**
 * @file    NpsSettingsManager.tsx
 * @scope   VIEW
 * @summary Advanced-settings panel for the NPS calc type: a 0-10 multi-slider defining the
 *          Detractors / Passives / Promoters bands, with a colour legend.
 * @consumes FieldsManager models, npsSettingsUtils (defaults/validation), npsColors
 * @produces `NpsSettings` component
 * @sideEffects None — controlled inputs; changes bubble up via `onFieldChange`.
 * @notes NPS always uses a fixed 0-10 scale (min/max hard-coded) regardless of the field's scale.
 */
import React, { useState, useEffect, useRef } from 'react';
import { MultiSlider } from '@cx/ui';
import type { RangeValue } from '@cx/ui/components/Slider/MultiSlider';
import { CalcSettingsPanelProps } from './PercentageSettingsManager';
import { NpsSettingsModel } from 'models/Enterprise/FieldsManagerModel';
import {
  buildDefaultNpsSettings,
  isValidNpsSettings,
} from '../../utils/views/partitionsManager/calculationsManager/npsSettingsUtils';
import DataValidationTip from 'components/DataValidation/DataValidationTip';
import { NPS_COLORS, NpsColorKey } from './npsColors';
import './NpsSettingsManager.scss';

const LEGEND_ITEMS: Array<{ key: NpsColorKey; label: string }> = [
  { key: 'detractors', label: 'Detractors' },
  { key: 'passives', label: 'Passives' },
  { key: 'promoters', label: 'Promoters' },
];

const NpsSettings: React.FC<CalcSettingsPanelProps> = ({ selectedField, onFieldChange }) => {
  // NPS always uses a 0-10 scale
  const minRating = 0;
  const maxRating = 10;
  const fieldGuid = selectedField.field ? selectedField.field.fieldGuid : undefined;

  const [npsSettings, setNpsSettings] = useState<NpsSettingsModel>(
    selectedField.npsSettings ?? buildDefaultNpsSettings(selectedField.field)
  );
  const [touched, setTouched] = useState(false);
  const [ranges, setRanges] = useState<RangeValue[]>([
    { from: minRating, to: npsSettings.detractorsMax },
    { from: npsSettings.detractorsMax + 1, to: npsSettings.promotersMin - 1 },
    { from: npsSettings.promotersMin, to: maxRating },
  ]);

  // Keep refs up-to-date so the unmount cleanup reads the latest values without stale closures
  const npsSettingsRef = useRef(npsSettings);
  const selectedFieldRef = useRef(selectedField);
  const onFieldChangeRef = useRef(onFieldChange);

  useEffect(() => {
    npsSettingsRef.current = npsSettings;
  }, [npsSettings]);

  useEffect(() => {
    selectedFieldRef.current = selectedField;
    onFieldChangeRef.current = onFieldChange;
  });

  // Sync when the parent changes the field (e.g. navigating back and forth)
  useEffect(() => {
    const settings = selectedField.npsSettings ?? buildDefaultNpsSettings(selectedField.field);
    setNpsSettings(settings);
    setRanges([
      { from: minRating, to: settings.detractorsMax },
      { from: settings.detractorsMax + 1, to: settings.promotersMin - 1 },
      { from: settings.promotersMin, to: maxRating },
    ]);
    setTouched(false);
  }, [fieldGuid]);

  // On unmount: ensure valid settings are persisted
  useEffect(() => {
    return () => {
      const { current: currentNpsSettings } = npsSettingsRef;
      const { current: currentSelectedField } = selectedFieldRef;
      const { field } = currentSelectedField;

      if (!isValidNpsSettings(currentNpsSettings, field)) {
        const defaults = buildDefaultNpsSettings(field);
        if (typeof onFieldChangeRef.current === 'function') {
          onFieldChangeRef.current({
            ...currentSelectedField,
            npsSettings: defaults,
          });
        }
      }
    };
  }, []);

  const handleRangeChange = (newRanges: RangeValue[]) => {
    setRanges(newRanges);
  };

  const handleMultiSliderChange = (values: number[]) => {
    if (values.length === 2) {
      // rangeMode="exclusive": values[0] = detractorsMax (end of Detractors), values[1] = promotersMin (start of Promoters)
      const detractorsMax = values[0];
      const promotersMin = values[1];
      const updated: NpsSettingsModel = {
        ...npsSettings,
        detractorsMax,
        promotersMin,
      };
      setNpsSettings(updated);
      setTouched(true);

      onFieldChange?.({
        ...selectedField,
        npsSettings: updated,
      });
    }
  };

  const isValid = isValidNpsSettings(npsSettings, selectedField.field);
  const showValidation = touched && !isValid;

  return (
    <div className="nps-settings-container">
      <div className="nps-settings-body">
        <div
          className={`nps-slider-wrapper${showValidation ? ' enterprise-container--invalid' : ''}`}
        >
          <MultiSlider
            min={minRating}
            max={maxRating}
            count={2}
            step={1}
            colors={[NPS_COLORS.detractors, NPS_COLORS.passives, NPS_COLORS.promoters]}
            showTooltips={false}
            showScale={false}
            rangeMode="exclusive"
            defaultValues={[npsSettings.detractorsMax, npsSettings.promotersMin]}
            onChange={handleMultiSliderChange}
            onRangeChange={handleRangeChange}
          />
          <div className="nps-slider-validation-row">
            {showValidation && (
              <DataValidationTip
                message="Invalid settings: Detractors max must be less than Promoters min."
                kind="error"
                placement="left"
              />
            )}
          </div>
        </div>
        <div className="nps-settings-info">
          <span>Drag the dots to adjust the NPS range</span>
        </div>
        <div className="nps-ranges-legend">
          {LEGEND_ITEMS.map(({ key, label }, i) => (
            <div key={key} className={`nps-legend-card nps-legend-card--${key}`}>
              <span className="nps-legend-card__label">{label}</span>
              <span className="nps-legend-card__range">
                {ranges[i] ? `${ranges[i].from} to ${ranges[i].to}` : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NpsSettings;
